import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { loadRemoteConfig } from "./remoteConfigService";
import { remoteDefaults } from "./remoteConfig";
import type { PlanId, RemoteConfigShape } from "../types/domain";

interface AppConfigContextValue {
  config: RemoteConfigShape;
  ready: boolean;
  featureEnabled: (name: string) => boolean;
  planLimits: (plan: PlanId) => RemoteConfigShape["plans"][PlanId];
}

const AppConfigContext = createContext<AppConfigContextValue | null>(null);

export function AppConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<RemoteConfigShape>(remoteDefaults);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    void loadRemoteConfig().then((next) => {
      if (active) setConfig(next);
    }).finally(() => { if (active) setReady(true); });
    return () => { active = false; };
  }, []);

  const value = useMemo<AppConfigContextValue>(() => ({
    config,
    ready,
    featureEnabled: (name) => config.features[name] !== false,
    planLimits: (plan) => config.plans[plan]
  }), [config, ready]);

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig(): AppConfigContextValue {
  const value = useContext(AppConfigContext);
  if (!value) throw new Error("AppConfigProvider missing");
  return value;
}
